import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

export default function EditProduct() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '', description: '', price: '', unit: 'kg', quantity: '', category: 'vegetables',
    organic: false, available: true, isSingleItem: false
  });
  const [images, setImages] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);

  useEffect(() => { fetchProduct(); }, [id]);

  const fetchProduct = async () => {
    try {
      const res = await fetch(`/api/products/${id}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (!res.ok) throw new Error('Product not found');
      const product = await res.json();
      setFormData({
        name: product.name, description: product.description, price: product.price,
        unit: product.unit, quantity: product.quantity, category: product.category,
        organic: product.organic, available: product.available !== false,
        isSingleItem: product.isSingleItem || false
      });
      setExistingImages(product.images || []);
    } catch (error) {
      console.error(error);
      alert('Failed to load product');
      navigate('/my-products');
    } finally { setLoading(false); }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    setImages(files);
    setPreviewUrls(files.map(f => URL.createObjectURL(f)));
  };

  const removeExistingImage = (index) => {
    setExistingImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const formDataToSend = new FormData();
    Object.keys(formData).forEach(key => formDataToSend.append(key, formData[key]));
    formDataToSend.append('existingImages', JSON.stringify(existingImages));
    images.forEach(img => formDataToSend.append('images', img));
    try {
      const res = await fetch(`/api/products/${id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: formDataToSend
      });
      if (!res.ok) throw new Error(await res.text());
      alert('Product updated');
      navigate('/my-products');
    } catch (error) {
      console.error(error);
      alert('Update failed');
    } finally { setSaving(false); }
  };

  if (loading) return <div className="text-center py-8">Loading...</div>;

  const categories = ['vegetables', 'fruits', 'dairy', 'meat', 'eggs', 'honey', 'livestock', 'other'];
  const units = ['kg', 'lb', 'each', 'bunch', 'dozen'];

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white p-8 rounded-lg shadow-md">
        <h1 className="text-2xl font-bold mb-6">Edit Product</h1>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div><label className="block text-gray-700 mb-2">Name *</label><input type="text" name="name" value={formData.name} onChange={handleChange} className="w-full border rounded-md px-3 py-2" required /></div>
          <div><label className="block text-gray-700 mb-2">Description *</label><textarea name="description" value={formData.description} onChange={handleChange} rows="4" className="w-full border rounded-md px-3 py-2" required /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-gray-700 mb-2">Price *</label><input type="number" name="price" value={formData.price} onChange={handleChange} step="0.01" min="0" className="w-full border rounded-md px-3 py-2" required /></div>
            <div><label className="block text-gray-700 mb-2">Unit *</label><select name="unit" value={formData.unit} onChange={handleChange} className="w-full border rounded-md px-3 py-2">{units.map(u => <option key={u} value={u}>{u}</option>)}</select></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-gray-700 mb-2">Quantity *</label><input type="number" name="quantity" value={formData.quantity} onChange={handleChange} min="0" disabled={formData.isSingleItem} className="w-full border rounded-md px-3 py-2" required /></div>
            <div><label className="block text-gray-700 mb-2">Category *</label><select name="category" value={formData.category} onChange={handleChange} className="w-full border rounded-md px-3 py-2">{categories.map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}</select></div>
          </div>
          <div className="flex items-center space-x-4">
            <label className="flex items-center"><input type="checkbox" name="organic" checked={formData.organic} onChange={handleChange} className="mr-2" /><span>Organic</span></label>
            <label className="flex items-center"><input type="checkbox" name="available" checked={formData.available} onChange={handleChange} className="mr-2" /><span>Available</span></label>
            <label className="flex items-center"><input type="checkbox" name="isSingleItem" checked={formData.isSingleItem} onChange={handleChange} className="mr-2" /><span>Unique item</span></label>
          </div>
          {existingImages.length > 0 && (
            <div><label className="block text-gray-700 mb-2">Current Images</label><div className="grid grid-cols-4 gap-2">{existingImages.map((img, idx) => <div key={idx} className="relative"><img src={`/uploads/${img}`} className="w-full h-20 object-cover rounded" /><button type="button" onClick={() => removeExistingImage(idx)} className="absolute top-0 right-0 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">×</button></div>)}</div></div>
          )}
          <div><label className="block text-gray-700 mb-2">Add New Images</label><input type="file" onChange={handleImageChange} multiple accept="image/*" className="w-full border rounded-md px-3 py-2" />
            {previewUrls.length > 0 && <div className="mt-2 grid grid-cols-4 gap-2">{previewUrls.map((url, i) => <img key={i} src={url} className="w-full h-20 object-cover rounded" />)}</div>}
          </div>
          <div className="flex gap-2"><button type="submit" disabled={saving} className="flex-1 bg-green-600 text-white py-2 rounded hover:bg-green-700">{saving ? 'Saving...' : 'Save Changes'}</button><button type="button" onClick={() => navigate('/my-products')} className="flex-1 bg-gray-300 text-gray-700 py-2 rounded hover:bg-gray-400">Cancel</button></div>
        </form>
      </div>
    </div>
  );
}
